import { createRouter, createWebHistory } from "vue-router";
import AppHome from "@/pages/Home.vue";
import ProductDetails from "@/pages/ProductDetails.vue";
import ProductList from "@/pages/ProductList.vue";
import ShoppingCart from "@/pages/ShoppingCart.vue";
import ProductForm from "@/components/admin/ProductForm.vue";
import LoginForm from "@/pages/LoginForm.vue";
import AdminProductList from "@/pages/admin/AdminProductList.vue";
import AdminOrderList from "@/pages/admin/AdminOrderList.vue";
import AdminOrderDetails from "@/pages/admin/AdminOrderDetails.vue";
import HowToGerminate from "@/pages/HowToGerminate.vue";
import AccountSettings from "@/pages/AccountSettings.vue";
import apiClient from '@/services/apiClient';
import { logout, hasAdminPermission } from '@/services/auth';
import RegisterForm from "@/pages/RegisterForm.vue";
import ActivityPage from "@/pages/ActivityPage.vue";  // Corregida esta línea
import BankForm from "@/pages/admin/BankForm.vue";
import AdminBankList from "@/pages/admin/AdminBankList.vue";
import WarrantyForm from '@/pages/WarrantyForm.vue';
import AdminWarrantyClaims from "@/pages/admin/AdminWarrantyClaims.vue";
import BrandList from '@/pages/admin/BrandList.vue';
import UserList from '@/pages/admin/UserList.vue';
import UserDetail from '@/pages/admin/UserDetail.vue';

const routes = [
  {
    path: "/",
    name: "Home",
    component: AppHome,
  },
  {
    path: "/mi-cuenta",
    name: "AccountSettings",
    component: AccountSettings,
    meta: { requiresAuth: true }
  },
  {
    path: "/como-germinar/",
    name: "HowToGerminate",
    component: HowToGerminate,
  },
  {
    path: "/producto/:id",
    name: "ProductDetails",
    component: ProductDetails,
  },
  {
    path: "/productos/",
    name: "ProductList",
    component: ProductList,
  },
  {
    path: "/carrito/",
    name: "ShoppingCart",
    component: ShoppingCart,
  },
  {
    path: "/garantia",
    name: "Warranty",
    component: WarrantyForm,
    meta: {
      title: 'Garantía de Germinación - Semillero del Oeste'
    }
  },
  {
    path: "/admin",
    name: "AdminArea",
    meta: { requiresAuth: true, requiresAdmin: true },
    children: [
      {
        path: "productos",
        name: "AdminProductList",
        component: AdminProductList
      },
      {
        path: "pedidos",
        name: "AdminOrderList",
        component: AdminOrderList
      },
      {
        path: "pedidos/:orderId",
        name: "AdminOrderDetails",
        component: AdminOrderDetails
      },
      {
        path: "productos/nuevo",
        name: "ProductForm",
        component: ProductForm
      },
      {
        path: "productos/editar/:productId",
        name: "ProductEdit",
        component: ProductForm
      },
      {
        path: "actividad",
        name: "ActivityLog",
        component: ActivityPage,
        meta: { 
          requiresAuth: true,
          requiresAdmin: true 
        }
      },
      {
        path: "bancos/nuevo",
        name: "BankForm",
        component: BankForm
      },
      {
        path: "bancos/editar/:id",
        name: "BankEdit",
        component: BankForm
      },
      {
        path: "bancos",
        name: "AdminBankList",
        component: AdminBankList
      },
      {
        path: "garantias",
        name: "AdminWarrantyClaims",
        component: AdminWarrantyClaims
      },
      {
        path: "marcas",
        name: "BrandList",
        component: BrandList,
        meta: { requiresAuth: true, requiresAdmin: true }
      },
      {
        path: "usuarios",
        name: "UserList",
        component: UserList,
        meta: { requiresAuth: true, requiresAdmin: true }
      },
      {
        path: "usuarios/:id",
        name: "UserDetail",
        component: UserDetail,
        meta: { requiresAuth: true, requiresAdmin: true }
      }
    ]
  },
  {
    path: "/login",
    name: "LoginForm",
    component: LoginForm,
    meta: { redirectIfAuth: true }
  },
  {
    path: "/crear-cuenta",
    name: "RegisterForm",
    component: RegisterForm,
    meta: { redirectIfAuth: true }
  },
  {
    path: "/:pathMatch(.*)*",
    redirect: "/"
  }
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior() {
    return { top: 0 }
  },
});

router.beforeEach(async (to, from, next) => {
  const isAuthenticated = !!localStorage.getItem('accessToken');
  
  // Redirigir si ya está autenticado e intenta acceder a login
  if (to.meta.redirectIfAuth && isAuthenticated) {
    return next('/mi-cuenta');
  }

  // Verificar si la ruta requiere autenticación
  if (to.meta.requiresAuth) {
    if (!isAuthenticated) {
      // Guardar la ruta intentada para redirigir después del login
      localStorage.setItem('intendedRoute', to.fullPath);
      return next({
        name: 'LoginForm',
        query: { redirect: to.fullPath }
      });
    }

    // Verificar si la ruta requiere permisos de admin
    if (to.meta.requiresAdmin) {
      try {
        // Verificar permisos desde el backend
        const hasAdmin = await hasAdminPermission();
        if (!hasAdmin) {
          console.warn('Acceso denegado: Usuario no tiene permisos de administrador');
          return next('/mi-cuenta');
        }
      } catch (error) {
        console.error('Error verificando permisos de admin:', error);
        // Si hay error de autenticación, redirigir a login
        if (error.response?.status === 401) {
          logout();
          return next({
            name: 'LoginForm',
            query: { redirect: to.fullPath }
          });
        }
        // Si hay otro error, redirigir a cuenta
        return next('/mi-cuenta');
      }
    }
  }

  next();
});

// Interceptor para manejar errores de autenticación
apiClient.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401) {
      logout();
      router.push({
        name: 'LoginForm',
        query: { redirect: router.currentRoute.value.fullPath }
      });
    }
    return Promise.reject(error);
  }
);

export default router;